import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Layers, FileText, ChevronRight, ChevronDown, Clock, Target,
  CheckCircle, Globe, Building2, Info, RefreshCw, Loader2,
  FolderKanban, GitBranch, Plus, Minus, Edit3, Lock, AlertTriangle,
  Shield, Link2, ListChecks
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { adaptWBSResponse } from '../utils/dataAdapters';
import { WidgetErrorBoundary } from './ErrorBoundary';
import { SkeletonTreeList } from './Skeleton';
import EmptyState from './EmptyState';

// ==========================================
// CONSTANTS
// ==========================================

const NODE_TYPE_ICONS = {
  phase: Layers,
  work_package: FolderKanban,
  task: CheckCircle,
};

const NODE_TYPE_COLORS = {
  phase: 'bg-purple-100 text-purple-700 border-purple-200',
  work_package: 'bg-blue-100 text-blue-700 border-blue-200',
  task: 'bg-green-100 text-green-700 border-green-200',
};

const CATEGORY_COLORS = {
  'Initiation': 'bg-indigo-100 text-indigo-700',
  'Planning': 'bg-blue-100 text-blue-700',
  'Execution': 'bg-amber-100 text-amber-700',
  'Change': 'bg-orange-100 text-orange-700',
  'Deployment': 'bg-green-100 text-green-700',
  'Closure': 'bg-gray-100 text-gray-700',
  'Discovery': 'bg-cyan-100 text-cyan-700',
  'Development': 'bg-blue-100 text-blue-700',
  'Integration': 'bg-purple-100 text-purple-700',
  'Testing': 'bg-amber-100 text-amber-700',
  'Handover': 'bg-green-100 text-green-700',
};

const DIFF_STATUS_COLORS = {
  added: 'border-l-4 border-l-green-500 bg-green-50',
  removed: 'border-l-4 border-l-red-500 bg-red-50 opacity-70',
  modified: 'border-l-4 border-l-amber-500 bg-amber-50',
  unchanged: '',
};

const DIFF_STATUS_ICONS = {
  added: Plus,
  removed: Minus,
  modified: Edit3,
  unchanged: null,
};

// ==========================================
// TOOLTIP COMPONENT
// ==========================================

const NodeTooltip = ({ node, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({ x: rect.right + 8, y: rect.top });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Count linked items
  const counts = useMemo(() => ({
    acceptance_criteria: node.acceptance_criteria?.length || 0,
    risks: node.risks?.length || 0,
    constraints: node.constraints?.length || 0,
    dependencies: node.dependencies?.length || 0,
  }), [node]);

  const hasLinkedItems = Object.values(counts).some(c => c > 0);

  return (
    <div 
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {showTooltip && hasLinkedItems && (
        <div 
          ref={tooltipRef}
          className="fixed z-50 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl max-w-xs"
          style={{ left: position.x, top: position.y }}
        >
          <div className="font-medium mb-2 text-gray-100">{node.name}</div>
          <div className="space-y-1">
            {counts.acceptance_criteria > 0 && (
              <div className="flex items-center gap-2">
                <ListChecks className="w-3.5 h-3.5 text-green-400" />
                <span>{counts.acceptance_criteria} Acceptance Criteria</span>
              </div>
            )}
            {counts.risks > 0 && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                <span>{counts.risks} Risks</span>
              </div>
            )}
            {counts.constraints > 0 && (
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-blue-400" />
                <span>{counts.constraints} Constraints</span>
              </div>
            )}
            {counts.dependencies > 0 && (
              <div className="flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5 text-purple-400" />
                <span>{counts.dependencies} Dependencies</span>
              </div>
            )}
          </div>
          {node.estimated_hours && (
            <div className="mt-2 pt-2 border-t border-gray-700 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span>Estimated: {node.estimated_hours}h</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================
// WBS TREE NODE COMPONENT (Enhanced)
// ==========================================

const WBSTreeNode = ({
  node,
  depth,
  isExpanded,
  onToggle,
  expandedIds,
  showTemplateOrigin = true,
  showDiffMarkers = false,
  isReadOnly = false,
  onNodeClick,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const Icon = NODE_TYPE_ICONS[node.node_type] || FileText;
  const typeColor = NODE_TYPE_COLORS[node.node_type] || 'bg-gray-100 text-gray-700 border-gray-200';
  const categoryColor = CATEGORY_COLORS[node.category] || 'bg-gray-100 text-gray-600';
  
  // Diff status
  const diffStatus = node.diff_status || 'unchanged';
  const diffColor = showDiffMarkers ? DIFF_STATUS_COLORS[diffStatus] : '';
  const DiffIcon = showDiffMarkers ? DIFF_STATUS_ICONS[diffStatus] : null;

  // Count linked items for indicator
  const linkedItemsCount = (node.acceptance_criteria?.length || 0) + 
    (node.risks?.length || 0) + 
    (node.constraints?.length || 0) + 
    (node.dependencies?.length || 0);

  return (
    <div className="select-none" data-testid={`wbs-node-${node.id}`}>
      <NodeTooltip node={node}>
        <div 
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
          className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer ${diffColor}`}
          onClick={() => onNodeClick?.(node)}
        >
          {/* Diff status indicator */}
          {showDiffMarkers && DiffIcon && (
            <span className={`p-0.5 rounded ${
              diffStatus === 'added' ? 'bg-green-200 text-green-700' :
              diffStatus === 'removed' ? 'bg-red-200 text-red-700' :
              diffStatus === 'modified' ? 'bg-amber-200 text-amber-700' : ''
            }`}>
              <DiffIcon className="w-3 h-3" />
            </span>
          )}

          {/* Expand/collapse */}
          {hasChildren ? (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onToggle(node.id);
              }}
              className="p-0.5 hover:bg-gray-200 rounded"
              data-testid={`wbs-toggle-${node.id}`}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}

          {/* Type badge */}
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${typeColor}`}>
            <Icon className="w-3 h-3" />
            {node.node_type?.replace('_', ' ')}
          </span>

          {/* Name */}
          <span className={`flex-1 text-sm truncate font-medium ${
            diffStatus === 'removed' ? 'line-through text-gray-400' : 'text-gray-800'
          }`}>
            {node.name}
          </span>

          {/* Linked items indicator */}
          {linkedItemsCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">
              {linkedItemsCount}
            </span>
          )}

          {/* Category badge */}
          {node.category && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${categoryColor}`}>
              {node.category}
            </span>
          )}

          {/* Estimated hours */}
          {node.estimated_hours && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {node.estimated_hours}h
            </span>
          )}

          {/* Template origin indicator */}
          {showTemplateOrigin && node.template_id && (
            <span 
              className="p-1 rounded hover:bg-gray-100"
              title={`From template: ${node.template_id}`}
            >
              <Globe className="w-3.5 h-3.5 text-blue-400" />
            </span>
          )}

          {/* Linked requirement indicator */}
          {node.requirement_id && (
            <span 
              className="p-1 rounded hover:bg-gray-100"
              title="Linked to requirement"
            >
              <GitBranch className="w-3.5 h-3.5 text-green-500" />
            </span>
          )}

          {/* Read-only indicator */}
          {isReadOnly && (
            <Lock className="w-3.5 h-3.5 text-gray-400" title="Read-only (baseline approved)" />
          )}
        </div>
      </NodeTooltip>

      {/* Render children recursively */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <WBSTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              isExpanded={expandedIds.has(child.id)}
              onToggle={onToggle}
              expandedIds={expandedIds}
              showTemplateOrigin={showTemplateOrigin}
              showDiffMarkers={showDiffMarkers}
              isReadOnly={isReadOnly}
              onNodeClick={onNodeClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// VIRTUALIZED WBS TREE FOR LARGE DATASETS
// ==========================================

const VirtualizedWBSTree = ({ 
  nodes, 
  expandedIds, 
  onToggle, 
  showTemplateOrigin,
  showDiffMarkers,
  isReadOnly,
  onNodeClick,
  containerHeight = 500,
}) => {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
  const containerRef = useRef(null);
  const ITEM_HEIGHT = 44; // Approximate height of each row

  // Flatten tree for virtualization
  const flattenedNodes = useMemo(() => {
    const result = [];
    const flatten = (items, depth = 0) => {
      for (const item of items) {
        result.push({ ...item, depth });
        if (expandedIds.has(item.id) && item.children?.length > 0) {
          flatten(item.children, depth + 1);
        }
      }
    };
    flatten(nodes);
    return result;
  }, [nodes, expandedIds]);

  // Handle scroll to update visible range
  const handleScroll = useCallback((e) => {
    const scrollTop = e.target.scrollTop;
    const start = Math.floor(scrollTop / ITEM_HEIGHT);
    const visibleCount = Math.ceil(containerHeight / ITEM_HEIGHT);
    setVisibleRange({
      start: Math.max(0, start - 5), // Buffer
      end: Math.min(flattenedNodes.length, start + visibleCount + 10),
    });
  }, [flattenedNodes.length, containerHeight]);

  // Use simple rendering for small trees
  if (flattenedNodes.length < 100) {
    return (
      <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg bg-white">
        <div className="p-2">
          {nodes.map(node => (
            <WBSTreeNode
              key={node.id}
              node={node}
              depth={0}
              isExpanded={expandedIds.has(node.id)}
              onToggle={onToggle}
              expandedIds={expandedIds}
              showTemplateOrigin={showTemplateOrigin}
              showDiffMarkers={showDiffMarkers}
              isReadOnly={isReadOnly}
              onNodeClick={onNodeClick}
            />
          ))}
        </div>
      </div>
    );
  }

  // Virtualized rendering for large trees
  const totalHeight = flattenedNodes.length * ITEM_HEIGHT;
  const visibleNodes = flattenedNodes.slice(visibleRange.start, visibleRange.end);
  const offsetY = visibleRange.start * ITEM_HEIGHT;

  return (
    <div 
      ref={containerRef}
      className="flex-1 overflow-y-auto border border-gray-200 rounded-lg bg-white"
      style={{ height: containerHeight }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div style={{ position: 'absolute', top: offsetY, left: 0, right: 0 }}>
          {visibleNodes.map(node => (
            <div 
              key={node.id}
              style={{ paddingLeft: `${node.depth * 24 + 12}px` }}
              className={`flex items-center gap-2 py-2 px-3 hover:bg-gray-50 ${
                showDiffMarkers && node.diff_status ? DIFF_STATUS_COLORS[node.diff_status] : ''
              }`}
            >
              {node.children?.length > 0 ? (
                <button 
                  onClick={() => onToggle(node.id)}
                  className="p-0.5 hover:bg-gray-200 rounded"
                >
                  {expandedIds.has(node.id) ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              ) : (
                <span className="w-5" />
              )}
              
              <span className={`flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${
                NODE_TYPE_COLORS[node.node_type] || 'bg-gray-100 text-gray-700'
              }`}>
                {React.createElement(NODE_TYPE_ICONS[node.node_type] || FileText, { className: 'w-3 h-3' })}
                {node.node_type?.replace('_', ' ')}
              </span>
              
              <span className="flex-1 text-sm truncate font-medium text-gray-800">
                {node.name}
              </span>
              
              {node.category && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  CATEGORY_COLORS[node.category] || 'bg-gray-100'
                }`}>
                  {node.category}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN WBS TREE COMPONENT (Enhanced)
// ==========================================

const WBSTree = ({
  projectId,
  tenantId,
  baselineId,
  showHeader = true,
  showTemplateOrigin = true,
  showDiffMarkers = false,
  diffData = null, // { added: [], removed: [], modified: [] }
  isReadOnly = false,
  onNodeSelect,
  containerHeight = 500,
}) => {
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Load WBS nodes
  useEffect(() => {
    if (tenantId && projectId && baselineId) {
      loadWBSNodes();
    } else {
      setNodes([]);
      setLoading(false);
    }
  }, [tenantId, projectId, baselineId]);

  const loadWBSNodes = async () => {
    setLoading(true);
    try {
      const res = await api.listWBSNodes(tenantId, projectId, baselineId, true);
      const adapted = adaptWBSResponse(res.data);
      let processedNodes = adapted.nodes || [];
      
      // Apply diff markers if provided
      if (diffData && showDiffMarkers) {
        processedNodes = applyDiffMarkers(processedNodes, diffData);
      }
      
      setNodes(processedNodes);
      
      // Auto-expand phase nodes
      const phaseIds = new Set();
      const findPhases = (items) => {
        for (const item of items) {
          if (item.node_type === 'phase') {
            phaseIds.add(item.id);
          }
          if (item.children) findPhases(item.children);
        }
      };
      findPhases(processedNodes);
      setExpandedIds(phaseIds);
    } catch (err) {
      console.error('Failed to load WBS nodes:', err);
      toast.error('Failed to load WBS structure');
    } finally {
      setLoading(false);
    }
  };

  // Apply diff markers to nodes
  const applyDiffMarkers = (nodes, diff) => {
    const addedIds = new Set(diff.added?.map(n => n.id) || []);
    const removedIds = new Set(diff.removed?.map(n => n.id) || []);
    const modifiedIds = new Set(diff.modified?.map(n => n.id) || []);

    const markNodes = (items) => {
      return items.map(item => {
        let status = 'unchanged';
        if (addedIds.has(item.id)) status = 'added';
        else if (removedIds.has(item.id)) status = 'removed';
        else if (modifiedIds.has(item.id)) status = 'modified';

        return {
          ...item,
          diff_status: status,
          children: item.children ? markNodes(item.children) : [],
        };
      });
    };

    return markNodes(nodes);
  };

  // Toggle expand/collapse
  const handleToggle = useCallback((id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Expand/collapse all
  const expandAll = useCallback(() => {
    const allIds = new Set();
    const collect = (items) => {
      for (const item of items) {
        if (item.children?.length > 0) {
          allIds.add(item.id);
          collect(item.children);
        }
      }
    };
    collect(nodes);
    setExpandedIds(allIds);
  }, [nodes]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  // Handle node click
  const handleNodeClick = useCallback((node) => {
    setSelectedNodeId(node.id);
    onNodeSelect?.(node);
  }, [onNodeSelect]);

  // Stats
  const stats = useMemo(() => {
    let phases = 0, workPackages = 0, tasks = 0, totalHours = 0;
    let added = 0, removed = 0, modified = 0;
    
    const countNodes = (items) => {
      for (const item of items) {
        if (item.node_type === 'phase') phases++;
        else if (item.node_type === 'work_package') workPackages++;
        else tasks++;
        
        if (item.estimated_hours) {
          totalHours += item.estimated_hours;
        }
        
        // Count diff statuses
        if (item.diff_status === 'added') added++;
        else if (item.diff_status === 'removed') removed++;
        else if (item.diff_status === 'modified') modified++;
        
        if (item.children) countNodes(item.children);
      }
    };
    countNodes(nodes);
    return { phases, workPackages, tasks, total: phases + workPackages + tasks, totalHours, added, removed, modified };
  }, [nodes]);

  if (loading) {
    return <SkeletonTreeList rows={6} />;
  }

  if (!baselineId) {
    return (
      <EmptyState
        icon={FolderKanban}
        title="No baseline selected"
        description="Select a baseline to view its WBS structure."
      />
    );
  }

  return (
    <WidgetErrorBoundary widgetName="WBS Tree">
      <div className="flex flex-col h-full" data-testid="wbs-tree">
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                Work Breakdown Structure
                {isReadOnly && (
                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                    <Lock className="w-3 h-3" />
                    Read-only
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500">
                {stats.total} nodes: {stats.phases} phases, {stats.workPackages} work packages
                {stats.totalHours > 0 && (
                  <span className="ml-2">• {stats.totalHours}h total</span>
                )}
              </p>
              {/* Diff summary */}
              {showDiffMarkers && (stats.added > 0 || stats.removed > 0 || stats.modified > 0) && (
                <div className="flex items-center gap-3 mt-1 text-xs">
                  {stats.added > 0 && (
                    <span className="flex items-center gap-1 text-green-600">
                      <Plus className="w-3 h-3" /> {stats.added} added
                    </span>
                  )}
                  {stats.removed > 0 && (
                    <span className="flex items-center gap-1 text-red-600">
                      <Minus className="w-3 h-3" /> {stats.removed} removed
                    </span>
                  )}
                  {stats.modified > 0 && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <Edit3 className="w-3 h-3" /> {stats.modified} modified
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                title="Expand all"
              >
                Expand
              </button>
              <button
                onClick={collapseAll}
                className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                title="Collapse all"
              >
                Collapse
              </button>
              <button
                onClick={loadWBSNodes}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        )}

        {/* WBS Tree */}
        {nodes.length === 0 ? (
          <EmptyState
            icon={FolderKanban}
            title="No WBS structure"
            description="Generate WBS from requirements to see the work breakdown structure."
          />
        ) : (
          <VirtualizedWBSTree
            nodes={nodes}
            expandedIds={expandedIds}
            onToggle={handleToggle}
            showTemplateOrigin={showTemplateOrigin}
            showDiffMarkers={showDiffMarkers}
            isReadOnly={isReadOnly}
            onNodeClick={handleNodeClick}
            containerHeight={containerHeight}
          />
        )}

        {/* Legend */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-medium text-gray-500 mb-2">Legend</p>
          <div className="flex items-center gap-4 flex-wrap text-xs">
            <span className="flex items-center gap-1 text-blue-500">
              <Globe className="w-3.5 h-3.5" />
              Template-generated
            </span>
            <span className="flex items-center gap-1 text-green-500">
              <GitBranch className="w-3.5 h-3.5" />
              Linked to requirement
            </span>
            {showDiffMarkers && (
              <>
                <span className="flex items-center gap-1 text-green-600">
                  <Plus className="w-3.5 h-3.5" />
                  Added
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <Minus className="w-3.5 h-3.5" />
                  Removed
                </span>
                <span className="flex items-center gap-1 text-amber-600">
                  <Edit3 className="w-3.5 h-3.5" />
                  Modified
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </WidgetErrorBoundary>
  );
};

export default WBSTree;
